import './impersonationPreInit';
import './unsubscribePreInit';
import React from 'react';
import { Outlet, redirect, createBrowserRouter, RouterProvider } from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import './index.scss';
import App from './App';
import reportWebVitals from './reportWebVitals';
import Auth from './pages/Auth/Auth';
import Profile from './pages/Profile/Profile';
import UserSettings from './pages/UserSettings/UserSettings';
import Payment from './pages/Payment/Payment';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import { ForgotPassword } from './pages/ForgotPassword/ForgotPassword';
import { RestorePassword } from './pages/RestorePassword/RestorePassword';
import { RestorePasswordGuard } from './components/guards/RestorePasswordGuard';
import { NotificationLayer } from './components/auth/NotificationLayer';
import Callback from './components/auth/Callback';
import { routesPath } from './common/constants/routesPath';
import Invitation from './pages/Invitation/Invitation';
import { InvitationGuard } from './components/guards/InvitationGuard';
import './i18n';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import { ConfigProvider } from 'antd';
import { themeConfigAuth } from './theming/themeConfigAuth';
import Report from './pages/Report/Report';
import TwoFactorAuth from './pages/TwoFactorAuth/TwoFactorAuth';
import Transactions from './pages/Transactions/Transactions';
import { constants } from './common/constants/constants';
import ReactGA from 'react-ga4';
import Congratulations from './pages/Congratulation/Congratulations';
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';
import ExtensionConfidencePrivacyPolicy from './pages/ExtensionConfidencePrivacyPolicy/Page';
import { PaymentLayout } from './components/payments/PaymentLayout';
import PrivacyAndTermsPage from './pages/PrivacyAndTermsPage/PrivacyAndTermsPage';
import AppLoader from './common/components/AppLoader';
import { LanguageProvider } from './context/LanguageProvider';
import Providers from './Providers';
import InternalReport from './pages/Report/InternalReport/InternalReport';
import SelectReportPage from './pages/SelectReport/SelectReportPage/SelectReportPage';
import CombinedReport from './pages/Report/CombinedReport';
import SharedReport from './pages/Report/SharedReport';
import Impersonate from './pages/Impersonate/Impersonate';
import { unsubscribeRoute } from './pages/Unsubscribe/unsubscribeRoute';
import { shouldLoadThirdPartyScripts } from './services/impersonationSession';
import { analyticsInitOptions, setAnalyticsPage } from './services/analytics';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

// an impersonation tab, and a tab that opened an emailed unsubscribe link, report nothing to analytics
if (shouldLoadThirdPartyScripts()) {
    // GA's own first page view is off and every hit carries a masked address: no reset token or share link
    // reaches Google (SC-QA-43)
    ReactGA.initialize(constants.GA_TRACKING_ID, analyticsInitOptions(window.location.pathname));
}

const router = createBrowserRouter([
    {
        path: routesPath.ROOT.PATH,
        element: <NotificationLayer />,
        children: [
            {
                path: routesPath.ROOT.PATH,
                element: (
                    <Providers isWithLanguageParam={false}>
                        <App />
                    </Providers>
                ),
                children: [
                    {
                        path: routesPath.ROOT.CONGRATULATIONS,
                        element: <Congratulations />,
                    },
                    {
                        path: routesPath.ROOT.DASHBOARD,
                        element: <Profile />,
                    },
                    // {
                    //     path: routesPath.ROOT.HELP,
                    //     element: <div>help page</div>,
                    // },
                    {
                        path: routesPath.ROOT.PAYMENT,
                        element: (
                            <PaymentLayout>
                                <Payment />
                            </PaymentLayout>
                        ),
                    },
                    {
                        path: routesPath.ROOT.SELECT_REPORT,
                        element: <SelectReportPage />,
                    },

                    {
                        path: routesPath.ROOT.REPORT,
                        element: <Report />,
                    },
                    {
                        path: routesPath.ROOT.INTERNAL_REPORT,
                        element: <InternalReport />,
                    },
                    {
                        path: routesPath.ROOT.COMBINED_REPORT,
                        element: <CombinedReport />,
                    },
                    {
                        path: routesPath.ROOT.USER_SETTINGS,
                        element: <UserSettings />,
                    },
                    {
                        path: routesPath.ROOT.TRANSACTIONS,
                        element: <Transactions />,
                    },
                    {
                        path: '*',
                        element: <NotFoundPage />,
                    },
                ],
            },
            {
                path: routesPath.AUTH.PATH,
                element: (
                    <Providers>
                        <ConfigProvider theme={themeConfigAuth}>
                            <Outlet />
                        </ConfigProvider>
                    </Providers>
                ),
                children: [
                    // redirecting to sign-in when opening /auth
                    {
                        index: true,
                        loader: async () => redirect('sign-in'),
                    },
                    {
                        path: routesPath.AUTH.SIGN_IN,

                        element: <Auth />,
                    },
                    {
                        path: routesPath.AUTH.TWO_FACTOR_AUTH,
                        element: <TwoFactorAuth />,
                    },
                    {
                        path: routesPath.AUTH.FORGOT_PASSWORD,
                        element: <ForgotPassword />,
                    },
                    {
                        path: routesPath.AUTH.RESTORE_PASSWORD,
                        element: (
                            <RestorePasswordGuard>
                                <RestorePassword />
                            </RestorePasswordGuard>
                        ),
                    },
                    {
                        path: routesPath.AUTH.INVITATION,
                        element: (
                            <InvitationGuard>
                                <Invitation />
                            </InvitationGuard>
                        ),
                    },
                    {
                        path: routesPath.AUTH.CALLBACK,
                        element: <Callback />,
                    },
                    {
                        path: '*',
                        element: <NotFoundPage />,
                    },
                ],
            },
            {
                path: routesPath.ERROR.PATH,
                element: <ErrorPage />,
            },
        ],
    },
    // outside Providers/AuthGuard: the tab has no session yet, and a refused code must not lead to sign-in
    {
        path: routesPath.PUBLIC.IMPERSONATE,
        element: (
            <ConfigProvider theme={themeConfigAuth}>
                <Impersonate />
            </ConfigProvider>
        ),
    },
    // the emailed unsubscribe link: public, outside Providers/AuthGuard, with its own language and auth theme
    unsubscribeRoute,
    {
        path: routesPath.PUBLIC.SAMPLE_REPORT,
        element: (
            <LanguageProvider>
                <Report sample={true} />
            </LanguageProvider>
        ),
    },
    {
        path: routesPath.PUBLIC.COMBINED_REPORT_EXAMPLE,
        element: (
            <LanguageProvider>
                <CombinedReport sample={true} />
            </LanguageProvider>
        ),
    },
    {
        path: routesPath.PUBLIC.INTERNAL_REPORT_EXAMPLE,
        element: (
            <LanguageProvider>
                <InternalReport sample={true} />
            </LanguageProvider>
        ),
    },
    {
        path: routesPath.PUBLIC.SHARED_REPORT,
        element: (
            <LanguageProvider>
                <SharedReport />
            </LanguageProvider>
        ),
    },
    {
        path: routesPath.PUBLIC.EXTENSION_CONFIDENCE_PRIVACY_POLICY,
        element: (
            <LanguageProvider>
                <ExtensionConfidencePrivacyPolicy />
            </LanguageProvider>
        ),
    },
    {
        path: routesPath.PUBLIC.PRIVACY_AND_TERMS,
        element: (
            <LanguageProvider>
                <PrivacyAndTermsPage />
            </LanguageProvider>
        ),
    },
]);

// the masked address of the new page is set before its first event or page view is sent
if (shouldLoadThirdPartyScripts()) {
    let lastPathname = window.location.pathname;
    router.subscribe(({ location }) => {
        if (location.pathname !== lastPathname) {
            lastPathname = location.pathname;
            setAnalyticsPage(location.pathname);
        }
    });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <React.Suspense fallback={<AppLoader />}>
        <RouterProvider router={router} />
    </React.Suspense>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
